import React, { useState, useEffect } from 'react';
import RichTextEditor from './RichTextEditor';
import './PersonalNoteModal.css';

/**
 * PersonalNoteModal - Create or edit a personal note
 * Now uses RichTextEditor for formatted note content
 */
const PersonalNoteModal = ({ note, onSave, onCancel }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (note) {
      setTitle(note.title || '');
      setContent(note.content || '');
      setCategory(note.category || null);
    }
  }, [note]);

  // Extract plain text from HTML for validation
  const getPlainText = (html) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.innerText || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate
    const plainText = getPlainText(content);
    if (!plainText.trim()) {
      setError('Note content is required');
      return;
    }
    if (plainText.length > 2000) {
      setError('Note content cannot exceed 2000 characters');
      return;
    }
    if (title && title.length > 100) {
      setError('Title cannot exceed 100 characters');
      return;
    }

    setSubmitting(true);
    try {
      await onSave(title || null, content, category || null);
    } catch (err) {
      setError(err.message || 'Failed to save note');
      setSubmitting(false);
    }
  };
  const titleCount = title.length;

  return (
    <div className="modal-overlay">
      <div className="personal-note-modal">
        <h3>{note ? 'Edit Note' : 'Create New Note'}</h3>

        {error && (
          <div className="modal-error">
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Title Input */}
          <div className="form-group">
            <label htmlFor="note-title">Title (Optional)</label>
            <input
              id="note-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give your note a title..."
              maxLength="100"
              disabled={submitting}
            />
            <div className="char-count">
              {titleCount}/100 characters
            </div>
          </div>

          {/* Category Selector */}
          <div className="form-group">
            <label htmlFor="note-category">Category (Optional)</label>
            <select
              id="note-category"
              value={category || ''}
              onChange={(e) => setCategory(e.target.value || null)}
              disabled={submitting}
            >
              <option value="">No category</option>
              <option value="reflection">💭 Reflection</option>
              <option value="analysis">📊 Analysis</option>
              <option value="progress">📈 Progress</option>
              <option value="personal">📝 Personal</option>
            </select>
          </div>

          {/* Content Editor */}
          <div className="form-group">
            <label htmlFor="note-content">Note Content</label>
            <RichTextEditor
              value={content}
              onChange={setContent}
              maxCharacters={2000}
              placeholder="Write your note here... Track observations, reflections, analysis, or personal coaching notes."
              disabled={submitting}
            />
          </div>

          {/* Buttons */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={submitting || !content.trim()}
            >
              {submitting ? 'Saving...' : note ? 'Update Note' : 'Create Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonalNoteModal;
